import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useCancelManagementPayment } from '../../../../hooks/payment/useCancelManagementPayment'
import { useConfirmPayment } from '../../../../hooks/payment/useConfirmPayment'

import { Link } from 'react-router-dom'

import {
  PaymentStatusBadge,
  RefundStatusBadge,
} from '../paymentBadge/PaymentBadge'
import Icon from '../../../ui/Icon'
import { Tabs } from '../../../tabs/Tabs'
import { ListRow } from '../../../listItem/listRow/ListRow'
import { ListCard } from '../../../listItem/listCard/ListCard'
import { ListCardStripe } from '../../../../types/listing'
import { ListingDetails } from '../../../listing/ListingDetails'
import { DropdownMenuItem } from '../../../dropdownMenu/DropdownMenu'
import { RowActions } from '../../../listItem/rowActions/RowActions'

import { CONFIG } from '../../../../config'
import { useApp } from '../../../../contexts/AppContext'
import { useConfirm } from '../../../../contexts/ConfirmContext'
import { BannerKind } from '../../../../types/contexts/AppContext'
import { ConfirmLayout } from '../../../../types/confirm/confirm'
import { DisplayType, SortDirectionType, SortType } from '../../../../types/listing'
import {
  PaymentFilterType,
  PaymentMetadata,
  PaymentMethod,
  PaymentStatus,
} from '../../../../types/payment/payment'
import {
  displayDate,
  displayLocalDateTimeHourMinute,
} from '../../../../utils/dateUtils'
import { checkoutPath, formatPoints } from '../../../../utils/booking/booking'

const STATUS_STRIPES: Record<PaymentStatus, ListCardStripe> = {
  [PaymentStatus.SUCCESS]: 'success',
  [PaymentStatus.AUTHORIZED]: 'info',
  [PaymentStatus.PAYMENT_PENDING]: 'warning',
  [PaymentStatus.FAILED]: 'danger',
  [PaymentStatus.CANCELED]: 'danger',
  [PaymentStatus.REFUNDED]: 'neutral',
  [PaymentStatus.EXPIRED]: 'neutral',
}

const VOIDED_STATUSES = [PaymentStatus.CANCELED, PaymentStatus.EXPIRED]

type PaymentListProps = {
  tab: PaymentFilterType
  onTabChange: (tab: PaymentFilterType) => void
}

export const PaymentList = ({ tab, onTabChange }: PaymentListProps) => {
  const { t, i18n } = useTranslation(['payments', 'bookings'])
  const { showBanner } = useApp()
  const { confirm } = useConfirm()
  const { confirmPayment } = useConfirmPayment()
  const { cancelManagementPayment } = useCancelManagementPayment()

  const additionalParams: Record<string, string> =
    tab === 'ALL' ? {} : { paymentMethod: tab }

  const tabs = [
    {
      value: 'ALL',
      label: t('payments.payment-list.filter.all'),
    },
    {
      value: PaymentMethod.CARD,
      label: t('payments.payment-list.filter.card'),
    },
    {
      value: PaymentMethod.CASH,
      label: t('payments.payment-list.filter.cash'),
    },
    {
      value: PaymentMethod.BANK_TRANSFER,
      label: t('payments.payment-list.filter.bank_transfer'),
    },
  ]

  const paymentMethodLabel = (paymentMethod: PaymentMethod) =>
    t(
      `bookings:booking.all-booking-list.payment-method.${paymentMethod.toLowerCase()}`
    )

  const renderPaymentMethod = (paymentMethod?: PaymentMethod) => {
    if (!paymentMethod) {
      return <span className="muted">&mdash;</span>
    }

    const label = paymentMethodLabel(paymentMethod)

    const badgeVariant = {
      [PaymentMethod.CARD]: 'badge-accent',
      [PaymentMethod.CASH]: 'badge-success',
      [PaymentMethod.BANK_TRANSFER]: 'badge-info',
    }[paymentMethod]

    return <span className={`badge ${badgeVariant}`}>{label}</span>
  }

  /** Money paid, plus any refund points spent in the same checkout. */
  const renderAmount = (payment: PaymentMetadata) => {
    const money = `${payment.amount} ${payment.currency?.toUpperCase() ?? ''}`.trim()
    return payment.pointsAmount
      ? `${money} + ${t('payments.points', { points: formatPoints(payment.pointsAmount) })}`
      : money
  }

  /** Short checkout reference (the booking number customers see in emails). */
  const getReferenceValue = (payment: PaymentMetadata) =>
    payment.referenceId ? payment.referenceId.slice(0, 8).toUpperCase() : ''

  const renderReference = (payment: PaymentMetadata) =>
    payment.referenceId ? (
      <Link className="mono" to={checkoutPath(payment.referenceId)}>
        {getReferenceValue(payment)}
      </Link>
    ) : (
      <span className="muted">&mdash;</span>
    )

  const handleListError = useCallback(
    (message: string) => {
      showBanner({
        kind: BannerKind.ERR,
        msg: message,
      })
    },
    [showBanner]
  )

  const getConfirmationItem = (payment: PaymentMetadata) => ({
    id: payment.id,
    title: payment.owner ?? renderAmount(payment),
    details: [
      {
        label: t('payments.payment-list.tabel-header.payment-id'),
        value: payment.id,
      },
      {
        label: t('payments.payment-list.tabel-header.reference'),
        value: getReferenceValue(payment) || '—',
      },
      {
        label: t('payments.confirmation.payment-method'),
        value: t(
          `payments.payment-list.filter.${payment.paymentMethod?.toLowerCase()}`
        ),
      },
      {
        label: t('payments.confirmation.amount'),
        value: renderAmount(payment),
      },
    ],
  })

  const handleConfirmPayment = (payment: PaymentMetadata) => {
    confirm({
      layout: ConfirmLayout.DETAILS,
      title: t('payments.confirmation.confirm.title'),
      subtitle: t('payments.confirmation.confirm.subtitle'),
      confirmLabel: t('payments.payment-list.actions.confirm'),
      backLabel: t('payments.confirmation.back'),
      returnTo: '/m/payments',
      itemCards: [getConfirmationItem(payment)],
      onConfirm: async () => {
        await confirmPayment(payment.id)
        showBanner({
          kind: BannerKind.OK,
          msg: t('payments.hook.successfully-confirmed'),
        })
      },
    })
  }

  const handleCancelPayment = (payment: PaymentMetadata) => {
    confirm({
      layout: ConfirmLayout.DETAILS,
      title: t('payments.confirmation.cancel.title'),
      subtitle: t('payments.confirmation.cancel.subtitle'),
      warning: t('payments.confirmation.cancel.warning'),
      confirmLabel: t('payments.payment-list.actions.cancel'),
      backLabel: t('payments.confirmation.back'),
      returnTo: '/m/payments',
      itemCards: [getConfirmationItem(payment)],
      onConfirm: async () => {
        await cancelManagementPayment(payment.id)
        showBanner({
          kind: BannerKind.OK,
          msg: t('payments.hook.successfully-canceled'),
        })
      },
    })
  }

  /** Actions on a payment, offered in its "more actions" menu (table row and card alike). */
  const menuItems = (payment: PaymentMetadata): DropdownMenuItem[] => {
    const isManualPayment =
      (payment.paymentMethod === PaymentMethod.CASH ||
        payment.paymentMethod === PaymentMethod.BANK_TRANSFER) &&
      payment.status === PaymentStatus.PAYMENT_PENDING

    if (!isManualPayment) {
      return []
    }

    return [
      {
        key: 'cancel',
        label: t('payments.payment-list.actions.cancel'),
        icon: 'x',
        danger: true,
        onClick: () => handleCancelPayment(payment),
      },
      {
        key: 'confirm',
        label: t('payments.payment-list.actions.confirm'),
        icon: 'check',
        onClick: () => handleConfirmPayment(payment),
      },
    ]
  }

  const renderActions = (payment: PaymentMetadata) => (
    <RowActions label={t('payments.payment-list.actions.more')} items={menuItems(payment)} />
  )

  const renderPayments = (item: PaymentMetadata, viewMode: DisplayType) => {
    const date = displayDate(item.createdDate, i18n.language)
    const time = displayLocalDateTimeHourMinute(item.createdDate)
    const status = (
      <span className="ph-row gap-1">
        <PaymentStatusBadge status={item.status} />
        {item.refundStatus && <RefundStatusBadge status={item.refundStatus} />}
      </span>
    )
    if (viewMode === DisplayType.LIST) {
      return (
        <ListRow
          key={item.id}
          cells={[
            {
              key: 'date',
              className: 'mono',
              content: `${date} ${time}`,
            },
            {
              key: 'owner',
              content: item.owner ?? <span className="muted">&mdash;</span>,
            },
            {
              key: 'reference',
              content: renderReference(item),
            },
            {
              key: 'amount',
              content: <div className="fw-semibold">{renderAmount(item)}</div>,
            },
            {
              key: 'payment-method',
              content: renderPaymentMethod(item.paymentMethod),
            },
            {
              key: 'status',
              content: status,
            },
          ]}
          actions={renderActions(item)}
        />
      )
    }

    return (
      <ListCard
        key={item.id}
        stripe={STATUS_STRIPES[item.status]}
        title={item.owner ?? renderAmount(item)}
        subtitle={
          <>
            <Icon name="calendar" size={12} />
            <span>{`${date} ${time}`}</span>
          </>
        }
        status={<PaymentStatusBadge status={item.status} />}
        menu={renderActions(item)}
        muted={VOIDED_STATUSES.includes(item.status)}
        fields={[
          {
            key: 'amount',
            label: t('payments.payment-list.tabel-header.amount'),
            value: <span className="fw-semibold">{renderAmount(item)}</span>,
          },
          {
            key: 'reference',
            label: t('payments.payment-list.tabel-header.reference'),
            value: renderReference(item),
          },
          {
            key: 'payment-method',
            label: t('payments.payment-list.tabel-header.payment-method'),
            value: item.paymentMethod
              ? paymentMethodLabel(item.paymentMethod)
              : '—',
            mono: false,
          },
          ...(item.refundStatus
            ? [
                {
                  key: 'refund',
                  label: t('payments.payment-list.tabel-header.refund'),
                  value: t(
                    `payments.refund-status.${item.refundStatus.toLowerCase()}`
                  ),
                  mono: false,
                },
              ]
            : []),
        ]}
      />
    )
  }

  return (
    <ListingDetails<PaymentMetadata>
      endpoint={`${CONFIG.BOUNCE_API}/api/v1/payments`}
      initialViewMode={DisplayType.LIST}
      // Newest payments first.
      defaultSortField={SortType.DATE}
      defaultSortDirection={SortDirectionType.DESC}
      displaySearch
      searchPlaceholder={t('payments.payment-list.search-placeholder')}
      filter={
        <Tabs
          tab={tab}
          tabs={tabs}
          onChange={value => onTabChange(value as PaymentFilterType)}
        />
      }
      additionalParams={additionalParams}
      tableColumns={[
        {
          key: 'date',
          label: t('payments.payment-list.tabel-header.date'),
        },
        {
          key: 'owner',
          label: t('payments.payment-list.tabel-header.owner'),
        },
        {
          key: 'reference',
          label: t('payments.payment-list.tabel-header.reference'),
        },
        {
          key: 'amount',
          label: t('payments.payment-list.tabel-header.amount'),
        },
        {
          key: 'payment-method',
          label: t('payments.payment-list.tabel-header.payment-method'),
        },
        {
          key: 'status',
          label: t('payments.payment-list.tabel-header.status'),
        },
        {
          key: 'actions',
          label: t('payments.payment-list.tabel-header.actions'),
          align: 'end',
        },
      ]}
      renderItem={renderPayments}
      onError={handleListError}
    />
  )
}
